/**
 * The A-Roll preloader: the wordmark's solid triangle turning in
 * thirds — ease in, land, breathe, turn again — so the wait has the
 * same fluid stop-start rhythm as the brand mark itself. Optional
 * cycling status lines underneath (each fades up into place; no
 * delayed opacity-0, so nothing can stick invisible on mobile).
 *
 * Colour rides on currentColor: set text-* on the parent and both the
 * mark and (dimmed) lines follow.
 *
 * This is now THE preloader for the whole app, not just A-Roll (Nick,
 * 2026-07-28: "any time we have a preload can we make sure it's that
 * rotating triangle"). The default size is 84 — 3x what it was — because
 * at 28 it read as a spinner in the corner of something rather than the
 * thing you're waiting on. Prefer `PageLoader` (components/States) over
 * calling this directly: it also handles the centring.
 *
 * On PictureUp pages (html.pu) the same component shows the
 * PictureUp_Symbol_Loop instead (brand kit v1, 2026-10-15): both marks
 * render and pictureup.css shows one, so the CRM keeps its triangle
 * wherever it mounts the shared A-Roll and Playback components.
 */
import { useEffect, useState } from 'react';
import { PictureUpSymbolLoop } from '@/components/PictureUpBrand';

export function ArollLoader({
  size = 84,
  lines,
  interval = 2800,
  className = '',
  delayMs = 1000,
}: {
  size?: number;
  /** Cycling status copy; omit for the bare mark. */
  lines?: string[];
  interval?: number;
  className?: string;
  /** Hold the mark back until the wait is actually a wait. Anything
   *  that resolves inside a second shows nothing at all rather than a
   *  spinner flash (Nick, 2026-07-30). Pass 0 to show immediately. */
  delayMs?: number;
}) {
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(delayMs <= 0);
  useEffect(() => {
    if (delayMs <= 0) return;
    const t = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(t);
  }, [delayMs]);
  useEffect(() => {
    if (!lines || lines.length < 2) return;
    const t = window.setInterval(() => setIdx((i) => (i + 1) % lines.length), interval);
    return () => window.clearInterval(t);
  }, [lines, interval]);

  if (!visible) return null;
  return (
    <div
      className={`flex flex-col items-center ${className}`}
      role="status"
      aria-live="polite"
      aria-label={lines?.[idx % (lines.length || 1)] ?? 'Loading'}
    >
      <span className="pu-loader-symbol text-[#f4f4f2]">
        <PictureUpSymbolLoop size={Math.round(size * 0.86)} />
      </span>
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        className="aroll-tri pu-loader-tri"
        aria-hidden
      >
        {/* Equilateral, side 18, centred in the 24 box: apex 12,4.206
            and base at y 19.794 (height = 18 x sqrt(3)/2 = 15.588).
            All three sides measure 18.000, so the mark holds its shape
            through every degree of the turn. */}
        <polygon points="12,4.206 21,19.794 3,19.794" fill="currentColor" />
      </svg>
      {lines && lines.length > 0 && (
        <p
          key={idx}
          className="aroll-tri-line mt-4 text-[12px] font-medium tracking-[0.08em] opacity-70"
        >
          {lines[idx % lines.length]}
        </p>
      )}
      <style>{`
        .aroll-tri {
          /* Rotate about the triangle's centroid, not the box centre.
             Equilateral centroid sits at 1/3 the height above the base:
             y = 14.598 of 24 = 60.83%. */
          transform-origin: 50% 60.83%;
          animation: aroll-tri-turn 3.2s infinite;
          /* A quarter darker than the colour it inherits — at page-loader
             size the full-strength mark read too bright (Nick,
             2026-07-28). Brightness (not alpha) so it darkens on light
             surfaces too instead of fading toward them. */
          filter: brightness(0.75);
        }
        @keyframes aroll-tri-turn {
          0%     { transform: rotate(0deg);   animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1); }
          21%    { transform: rotate(120deg); }
          33.33% { transform: rotate(120deg); animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1); }
          54.33% { transform: rotate(240deg); }
          66.66% { transform: rotate(240deg); animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1); }
          87.66% { transform: rotate(360deg); }
          100%   { transform: rotate(360deg); }
        }
        .aroll-tri-line {
          animation: aroll-tri-line-in 420ms ease-out both;
        }
        @keyframes aroll-tri-line-in {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 0.7; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .aroll-tri { animation: none; }
          .aroll-tri-line { animation: none; }
        }
      `}</style>
    </div>
  );
}
